import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { trimIdentifierFields } from "../utils/trimFormFields";

interface BilingualMasterDataApi {
  add: (data: any) => Promise<any>;
  update: (data: any, id: any) => Promise<any>;
  remove: (data: any, id: any) => Promise<any>;
  list: () => Promise<any>;
  search: (data: any, key: string) => Promise<any>;
}

interface UseBilingualMasterDataConfig<T extends Record<string, any>> {
  emptyData: T;
  englishField: string;
  spanishField: string;
  api: BilingualMasterDataApi;
  getEditId?: (item: any) => any;
  getDeleteId?: (iconId: any) => any;
  sortList?: ((list: any[]) => any[]) | null;
  setDataOnShow?: boolean;
  clearBeforeDelete?: boolean;
  collectTransform?: (item: any, emptyData: T) => T;
}

export function useBilingualMasterData<T extends Record<string, any>>(
  config: UseBilingualMasterDataConfig<T>,
) {
  const {
    emptyData,
    englishField,
    spanishField,
    api,
    getEditId = (item: any) => item.id,
    getDeleteId = (iconId: any) => iconId.id,
    sortList = null,
    setDataOnShow = false,
    clearBeforeDelete = false,
    collectTransform,
  } = config;

  const location = useLocation();
  const [isError, setIsError] = useState(false);
  const [validated, setValidated] = useState(false);
  const [cp] = useState(false);
  const [icon_id, setIcon_id] = useState<any>("");
  const [list, setList] = useState<any[]>([]);
  const [show, setShow] = useState(false);
  const [LoadData, setLoadData] = useState(false);
  const [oldData, setOldData] = useState<any>({});
  const [oldValues, setOldValues] = useState<any>({});
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [matches, setMatches] = useState<any[]>([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [data, setData] = useState<T>({ ...emptyData });
  const [searchKey, setSearchKey] = useState(
    () => (location.state as any)?.searchKeyword || "",
  );

  const handleClose = () => setShow(false);
  const handleCloseAlert = () => setDeleteAlert(false);

  const handleShow = (item: any) => {
    if (item && item.roasters_count > 0) {
      setDeleteAlert(true);
    } else {
      setShow(true);
      setIcon_id(item);
      if (setDataOnShow) {
        setData(item);
      }
    }
  };

  const handleChanges = (e: any) => {
    const { name, value } = e.target as { name: string; value: string };
    const d: Record<string, any> = { ...data };
    const o: Record<string, any> = { ...oldData };
    if (name === englishField) {
      o[englishField] = oldValues[englishField];
    } else if (name === spanishField) {
      o[spanishField] = oldValues[spanishField];
    }
    setOldData(o);
    d[name] = value;
    setData(d as T);
  };

  const clearfield = () => {
    setData({ ...emptyData });
    setIcon_id("");
  };

  const fetchList = async () => {
    setLoadData(true);
    try {
      const response: any = await api.list();
      const rows = response.data || [];
      setList(sortList ? sortList(rows) : rows);
    } catch (error: any) {
      toast.error(error.toString());
    } finally {
      setLoadData(false);
    }
  };

  const handleAfterSubmit = async (response: any) => {
    fetchList();
    toast.success(response.msg);
    clearfield();
  };

  const handleSubmit = (e: any) => {
    const form = e.currentTarget;
    e.preventDefault();
    e.stopPropagation();

    if (form.checkValidity()) {
      const payload = trimIdentifierFields(data, [englishField, spanishField]);
      if (icon_id === "") {
        api
          .add(payload)
          .then(async (res: any) => {
            if (res.success) {
              if (res.matches && res.matches.length > 0) {
                setMatches(res.matches);
                setShowConfirmationModal(true);
              } else {
                handleAfterSubmit(res);
              }
            } else {
              toast.error(res.msg);
            }
          })
          .catch((error: any) => {
            console.log(error);
            toast.error(error.toString());
          });
      } else {
        api
          .update(payload, icon_id)
          .then(async (response: any) => {
            handleAfterSubmit(response);
          })
          .catch((error: any) => {
            toast.error(error.toString());
          });
      }
    } else {
      setValidated(true);
    }
  };

  const handleConfirmSubmit = () => {
    if (matches.length > 0) {
      const payload = trimIdentifierFields(data, [englishField, spanishField]);
      api
        .add({ ...payload, forceSave: true })
        .then((res: any) => {
          if (res.success) {
            handleAfterSubmit(res);
          } else {
            toast.error(res.msg);
          }
        })
        .catch((error: any) => {
          console.log(error);
          toast.error(error.toString());
        });
      setShowConfirmationModal(false);
    }
  };

  const CollectData = (item: any) => {
    setIcon_id(getEditId(item));
    setOldValues(item);
    if (collectTransform) {
      setData(collectTransform(item, { ...emptyData }));
    } else {
      setData(item);
    }
    setValidated(false);
    const form = document.getElementById("container-scroller");
    form?.scrollIntoView();
  };

  const DeleteData = async () => {
    const deleteId = getDeleteId(icon_id);
    if (clearBeforeDelete) {
      clearfield();
    }
    await api
      .remove(data, deleteId)
      .then(async (response: any) => {
        fetchList();
        setShow(false);
        setIcon_id("");
        toast.success(response.msg);
      })
      .catch((error: any) => {
        setShow(false);
        setIcon_id("");
        toast.error(error.toString());
      });
  };

  const SearchData = (event: any) => {
    setLoadData(true);
    const key = typeof event.target.value === "string"
      ? event.target.value.trim()
      : event.target.value;
    setSearchKey(key);
    if (key) {
      console.warn(key);
      api
        .search(data, key)
        .then(async (response: any) => {
          setList(response.data);
          setLoadData(false);
        })
        .catch((error: any) => {
          setLoadData(false);
          toast.error(error.toString());
        });
    } else {
      setLoadData(false);
      fetchList();
    }
  };

  return {
    isError,
    setIsError,
    validated,
    cp,
    icon_id,
    list,
    show,
    LoadData,
    oldData,
    deleteAlert,
    matches,
    showConfirmationModal,
    setShowConfirmationModal,
    data,
    handleClose,
    handleCloseAlert,
    handleShow,
    handleChanges,
    handleSubmit,
    handleConfirmSubmit,
    CollectData,
    DeleteData,
    SearchData,
    fetchList,
    clearfield,
    searchKey,
  };
}
